import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  Building,
  Mail,
  Phone,
  Globe,
  Tag,
  ChevronDown,
  Users,
  User,
  Folder,
  FileText,
  MapPin,
  Factory,
  Hash,
  BadgeDollarSign,
  Linkedin,
  Instagram,
  Twitter,
  Facebook,
  ChevronUp,
} from "lucide-react";
import { AskAiButton } from "../../../../chat";
import clsx from "clsx";
import LabelsSelect from "../../../../components/LabelsSelect";
import TaskAction from "../../../../actions/TaskAction";
import BucketAction from "../../../../actions/BucketAction";
import EditableDetailRow from "./../EditableDetailRow";
import WebsiteInputField from "./../WebsiteInputField";
import EmailInputField from "./../EmailInputField";
import PhoneInputField from "./../PhoneInputField";
import SelectInline from "./../SelectInline";
import AutoTextArea from "./../AutoTextArea";
import DomainsSelect from "../../../../components/DomainsSelect";
import Avatar from "../../../../components/Avatar";
import AddressGroupField from "./../AddressGroupField";
import CustomField from "../../../../main/CustomField";
import { FileCode } from "lucide-react";
import AvatarUploadRow from "./../AvatarUploadRow";
import InputInline from "../../../../components/InputInline";
import AmountInputField from "./../AmountInputField";
import AssigneeSelectV2 from "../../../../components/AssigneeSelect";
import DetailPanelSkeleton from "./DetailPanelSkeleton";
import BucketSelect from "../../../../components/BucketSelect";
import { formatAmount } from "../../../../api/DataTypeUtils";
import Card from "../../../../components/ui/Card";
import ItemSelect from "../../../../components/ItemSelect";
import { navigateToItem } from "../../../../Navigator";
import ItemLinkAction from "../../../../actions/ItemLinkAction";

interface AccountDetailPanelProps {
  projectId: string;
  contentType: string; // expect "COMPANY"
}

// Helpers
const toStr = (v: any) => (v === null || v === undefined ? "" : String(v));
const normalizeOptions = (opts: any[]): { value: string; label: string }[] => {
  if (!Array.isArray(opts)) return [];
  return opts.map((o: any) => ({
    value: toStr(o?.value),
    label: toStr(o?.label),
  }));
};
const toPlain = (v: any) => (typeof v?.toJS === "function" ? v.toJS() : v);

// Extract username from social media URL
// e.g., "https://www.instagram.com/hoanganh993/" => "hoanganh993"
const extractSocialUsername = (url: string): string => {
  if (!url) return "";
  // Remove trailing slash and get the last path segment
  const cleanUrl = url.replace(/\/+$/, "");
  // Try to extract username from URL patterns
  const patterns = [
    /(?:instagram\.com|facebook\.com|twitter\.com|x\.com|linkedin\.com)\/(?:in\/)?([^/?]+)/i,
    /([^/]+)$/, // fallback: get last segment
  ];
  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return url;
};

export const AccountDetail = ({ projectId, contentType, isLoading, task, visibleRowNumber }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const members = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || fromJS([])
  );

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";
  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType)) || fromJS([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- industriesRaw used in useMemo
  const industriesRaw =
    useSelector((state: any) => state.ruleReducer.getIn(["industry"]))?.toJS() || [];

  // Ensure buckets are loaded
  useEffect(() => {
    if (projectId && contentType && (!buckets || buckets.size === 0)) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
    }
  }, [dispatch, projectId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = (task?.get?.("data") as any) || fromJS({});
  const getVal = (fieldId: string) => data.getIn([fieldId, "value"]);

  // Normalized options for selects
  const industryOptions = useMemo(() => normalizeOptions(industriesRaw), [industriesRaw]);

  // Current values
  const accountNameRaw = getVal("name");
  const accountName = accountNameRaw || t("Untitled");
  const iconUrl = task.get("iconUrl") || "";
  const email = getVal("email") || "";
  const phone = getVal("phone") || "";
  const website = getVal("website") || "";
  const description = getVal("description") || "";
  const estimatedArrRaw = getVal("estimatedArr");
  const fundingRaisedRaw = getVal("fundingRaised");
  const estimatedArr = Number(estimatedArrRaw || 0);
  const fundingRaised = Number(fundingRaisedRaw || 0);
  const employeeRange = getVal("employeeRange") || "";
  const street = getVal("street") || "";
  const country = getVal("country");
  const state = getVal("state");
  const city = getVal("city");
  const postcode = getVal("postcode") || "";

  // Extract labels for display (similar to DealForm.tsx pattern)
  const countryLabel = (() => {
    const plain = toPlain(country);
    if (plain && typeof plain === "object") {
      return plain.label ?? plain.value ?? "";
    }
    return plain || "";
  })();
  const stateLabel = (() => {
    const plain = toPlain(state);
    if (plain && typeof plain === "object") {
      return plain.label ?? plain.value ?? "";
    }
    return plain || "";
  })();
  const cityLabel = (() => {
    const plain = toPlain(city);
    if (plain && typeof plain === "object") {
      return plain.label ?? plain.value ?? "";
    }
    return plain || "";
  })();
  const industry = getVal("industry") || "";

  // Social media fields
  const linkedIn = getVal("linkedIn") || "";
  const instagram = getVal("instagram") || "";
  const twitter = getVal("twitter") || "";
  const facebook = getVal("facebook") || "";

  // Industry label
  const industryLabel = useMemo(() => {
    const f = industryOptions.find((o) => toStr(o.value) === toStr(industry));
    if (f?.label) return f.label;
    if (industry) return toStr(industry);
    return <span className="text-gray-400">{t("Set a value...")}</span>;
  }, [industryOptions, industry, t]);

  // Assignee display names
  const assigneeIds: string[] = (() => {
    const toIdArr = (v: any): string[] => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
      if (typeof v?.toJS === "function") {
        const x = v.toJS();
        if (Array.isArray(x)) return x.map((s: any) => String(s)).filter(Boolean);
        if (typeof x === "string")
          return x
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean);
        return [];
      }
      if (typeof v === "string")
        return v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [];
    };
    return toIdArr(getVal("assignees")).map((s) => String(s));
  })();
  const assigneeMatches = assigneeIds.map((id) => {
    const mByAccount = members.find((m: any) => String(m.get("accountId")) === String(id));
    const mById = mByAccount || members.find((m: any) => String(m.get("id")) === String(id));
    const mByValue = mById || members.find((m: any) => String(m.get("value")) === String(id));
    const found = mByValue || null;
    return {
      accountId: String(id),
      displayName:
        found?.get?.("displayName") || found?.get?.("name") || found?.get?.("email") || String(id),
      email: found?.get?.("email"),
      avatarUrl: found?.get?.("avatarUrl"),
    };
  });

  // Render assignees with avatars
  const renderAssigneeDisplay = () => {
    if (assigneeMatches.length === 0) {
      return <span className="text-gray-500">{t("Unassigned")}</span>;
    }

    const maxVisible = 2;
    const visibleAssignees = assigneeMatches.slice(0, maxVisible);
    const remainingCount = assigneeMatches.length - maxVisible;

    return (
      <div className="flex items-center gap-x-2 overflow-hidden">
        {visibleAssignees.map((user) => (
          <div
            key={`assignee_${user.accountId}`}
            className="flex items-center space-x-1.5 shrink-0"
          >
            <Avatar sizeIcon={4} avatarUrl={user.avatarUrl} displayName={user.displayName} />
            <span className="text-[12px] leading-4 font-medium text-gray-800 whitespace-nowrap">
              {user.displayName}
            </span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="flex items-center space-x-1.5 shrink-0">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-gray-600">+{remainingCount}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Format website/URL with link style
  const formatWebsiteDisplay = (url: string) => {
    if (!url) return <span className="text-gray-400">{t("Set a value...")}</span>;
    const displayUrl =
      url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
    return (
      <a
        href={displayUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-700 underline decoration-gray-200 underline-offset-2 cursor-pointer"
        onClick={(e) => {
          // Allow Ctrl+Click or middle-click to open link
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let default behavior happen
          }
          // Otherwise, prevent default and let event bubble to open edit mode
          e.preventDefault();
        }}
      >
        {url}
      </a>
    );
  };

  // Format email with link style
  const formatEmailDisplay = (emailValue: string) => {
    if (!emailValue) return <span className="text-gray-400">{t("Set a value...")}</span>;
    return (
      <a
        href={`mailto:${emailValue}`}
        className="text-blue-600 hover:text-blue-700 underline decoration-gray-200 underline-offset-2 cursor-pointer"
        onClick={(e) => {
          // Allow Ctrl+Click or middle-click to open link
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let default behavior happen
          }
          // Otherwise, prevent default and let event bubble to open edit mode
          e.preventDefault();
        }}
      >
        {emailValue}
      </a>
    );
  };

  // Format phone with link style
  const formatPhoneDisplay = (phoneValue: string) => {
    if (!phoneValue) return <span className="text-gray-400">{t("Set a value...")}</span>;
    return (
      <a
        href={`tel:${phoneValue}`}
        className="text-blue-600 hover:text-blue-700 underline decoration-gray-200 underline-offset-2 cursor-pointer"
        onClick={(e) => {
          // Allow Ctrl+Click or middle-click to open link
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let default behavior happen
          }
          // Otherwise, prevent default and let event bubble to open edit mode
          e.preventDefault();
        }}
      >
        {phoneValue}
      </a>
    );
  };

  // Update
  const handleUpdateField = async (fieldId: string, fieldValue: any) => {
    const itemId = task.get("id");
    if (itemId) {
      await dispatch(TaskAction.updateTask(itemId, fieldId, fieldValue));
    }
  };

  // Get task field value (for custom fields)
  const getTaskFieldValue = (fieldId: string) => {
    return getVal(fieldId);
  };

  // Render custom fields
  const renderCustomFields = () => {
    if (!fields) return [];

    const customFields = fields.filter(
      (field: any) => field.get("isCustomField") && field.get("formType") === contentType
    );

    if (!customFields || customFields.size === 0) return [];

    return customFields
      .map((customField: any) => {
        const fieldId = customField.get("id");
        const fieldData = {
          ...customField.toJS(),
          value: getTaskFieldValue(fieldId),
        };

        // Format value for display
        const displayValue = () => {
          const val = getTaskFieldValue(fieldId);
          if (val === null || val === undefined || val === "") {
            return <span className="text-gray-400">{t("Set a value...")}</span>;
          }
          if (customField.get("type") === "boolean") {
            return val === "yes" || val === true ? t("Yes") : t("No");
          }
          if (customField.get("type") === "datetime") {
            return val ? new Date(val).toLocaleDateString() : t("Set a value...");
          }
          return String(val);
        };

        return buildRow(
          `custom_${fieldId}`,
          <FileCode className="w-4 h-4 text-gray-500" />,
          t(customField.get("label") || fieldId),
          <div className="w-full">
            <CustomField
              field={fieldData}
              variant="inline"
              onChange={(value: any) => {
                handleUpdateField(fieldId, value);
              }}
            />
          </div>,
          displayValue()
        );
      })
      .toArray();
  };

  // Row builder
  const buildRow = (
    key: string,
    icon: React.ReactNode,
    label: string,
    editComponent: React.ReactNode,
    valueContent: React.ReactNode,
    usePopover?: boolean,
    actions?: ("edit" | "copy" | "show-all")[]
  ) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <EditableDetailRow
        label={label}
        editComponent={editComponent}
        usePopover={usePopover}
        actions={actions}
      >
        {valueContent}
      </EditableDetailRow>
    </div>
  );

  const buildRowContact = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <ItemSelect
            projectId={projectId}
            contentType="CONTACT"
            type="INLINE"
            value={getTaskFieldValue("contact")?.toJS()}
            onChange={async (contact: any) => {
              await handleUpdateField("contact", contact);
              dispatch(ItemLinkAction.getItemLink(task.get("id")));
            }}
            placeholder={t("No contact")}
            isClearable
            onOpenItem={(contact: any) => {
              const itemData = fromJS({
                id: contact.value || contact.id,
                type: "CONTACT",
                projectId: projectId,
              });
              navigateToItem({ item: itemData, navigate });
            }}
          />
        </div>
      </div>
    </div>
  );

  const buildRowLabels = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <LabelsSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getVal("labels")?.toJS() || []}
            onChange={(labelIds: string[]) => handleUpdateField("labels", labelIds)}
            placeholder={t("Select labels")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowDomains = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full py-0.5">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <DomainsSelect
            type="INLINE"
            value={getVal("domains")?.toJS() || []}
            onChange={(domains: string[]) => handleUpdateField("domains", domains)}
            placeholder={t("Set domains (e.g., example.com)")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowBucket = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <BucketSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getTaskFieldValue("bucketId")}
            onChange={(bucketId: string) => handleUpdateField("bucketId", bucketId)}
            placeholder={t("Select...")}
          />
        </div>
      </div>
    </div>
  );

  const itemId = String(task.get("id") || "");

  const rows: React.ReactNode[] = [
    // Logo field (shared component)
    <AvatarUploadRow
      key="iconUrl"
      itemId={itemId}
      iconUrl={String(iconUrl)}
      displayName={accountName}
      type="COMPANY"
      onSuccess={() => dispatch(TaskAction.getItem(itemId))}
    />,
    // Basic Information
    buildRow(
      "accountName",
      <Building className="w-4 h-4 text-gray-500" />,
      t("Account Name"),
      <InputInline
        value={accountNameRaw || ""}
        onFinish={(val: string) => {
          if (val && val.trim()) {
            handleUpdateField("name", val);
          }
        }}
        variant="inline"
      />,
      accountName
    ),
    buildRowDomains("domains", <Globe className="w-4 h-4 text-gray-500" />, t("Domains")),
    buildRow(
      "description",
      <FileText className="w-4 h-4 text-gray-500" />,
      t("Description"),
      <div className="w-full rounded-md min-h-[60px]">
        <AutoTextArea
          rows={3}
          placeholder={t("Add a description...")}
          value={description}
          onChange={(val: string) => handleUpdateField("description", val)}
          className="text-[12px] border-0 leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none resize-none"
          autoFocus={true}
        />
      </div>,
      description ? (
        <span className="line-clamp-2">{description}</span>
      ) : (
        <span className="text-gray-500">{t("Set a value...")}</span>
      ),
      true,
      description ? ["show-all"] : undefined
    ),
    buildRow(
      "estimatedArr",
      <BadgeDollarSign className="w-4 h-4 text-gray-500" />,
      t("Estimated ARR"),
      <AmountInputField
        value={estimatedArrRaw}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("estimatedArr", val)}
      />,
      estimatedArrRaw ? (
        formatAmount(i18n.language, estimatedArr, currency, "0$")
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      )
    ),
    buildRow(
      "fundingRaised",
      <BadgeDollarSign className="w-4 h-4 text-gray-500" />,
      t("Funding raised"),
      <AmountInputField
        value={fundingRaisedRaw}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("fundingRaised", val)}
      />,
      fundingRaisedRaw ? (
        formatAmount(i18n.language, fundingRaised, currency, "0$")
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      )
    ),
    buildRow(
      "employeeRange",
      <Users className="w-4 h-4 text-gray-500" />,
      t("Employee range"),
      <InputInline
        value={employeeRange}
        onFinish={(val: string) => handleUpdateField("employeeRange", val)}
        placeholder={t("Set employee range")}
        variant="inline"
      />,
      employeeRange || <span className="text-gray-400">{t("Set a value...")}</span>
    ),
    buildRowContact("contact", <User className="w-4 h-4 text-gray-500" />, t("Primary Contact")),
    buildRowLabels("labels", <Tag className="w-4 h-4 text-gray-500" />, t("Labels")),
    buildRow(
      "assignees",
      <Users className="w-4 h-4 text-gray-500" />,
      t("Owners"),
      <AssigneeSelectV2
        value={getVal("assignees")}
        autoOpen
        showTriggerHeight={true}
        customTrigger={renderAssigneeDisplay()}
        className="z-50"
        multiple={true}
        onChange={(val) => {
          const ids = Array.isArray(val)
            ? val
            : String(val || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
          handleUpdateField("assignees", ids);
        }}
      />,
      renderAssigneeDisplay()
    ),
    // Contact Information
    buildRow(
      "website",
      <Globe className="w-4 h-4 text-gray-500" />,
      t("Website"),
      <WebsiteInputField
        value={website}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("website", val)}
      />,
      formatWebsiteDisplay(website),
      undefined,
      ["edit", "copy"]
    ),
    buildRow(
      "email",
      <Mail className="w-4 h-4 text-gray-500" />,
      t("Email"),
      <EmailInputField
        value={email}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("email", val)}
      />,
      formatEmailDisplay(email),
      undefined,
      ["edit", "copy"]
    ),
    buildRow(
      "phone",
      <Phone className="w-4 h-4 text-gray-500" />,
      t("Phone"),
      <PhoneInputField
        value={phone}
        onFinish={(val: string) => handleUpdateField("phone", val)}
        variant="inline"
      />,
      formatPhoneDisplay(phone),
      undefined,
      ["edit", "copy"]
    ),
    buildRow(
      "address",
      <MapPin className="w-4 h-4 text-gray-500" />,
      t("Address"),
      <AddressGroupField
        street={street}
        city={city}
        state={state}
        country={country}
        onStreetChange={(val: string) => handleUpdateField("street", val)}
        onCityChange={(val: string) => handleUpdateField("city", val)}
        onStateChange={(val: string) => handleUpdateField("state", val)}
        onCountryChange={(val: string) => handleUpdateField("country", val)}
      />,
      (() => {
        const parts: string[] = [];
        if (street) parts.push(street);
        if (cityLabel) parts.push(cityLabel);
        if (stateLabel) parts.push(stateLabel);
        if (countryLabel) parts.push(countryLabel);
        return parts.length > 0 ? (
          parts.join(", ")
        ) : (
          <span className="text-gray-400">{t("Set a value...")}</span>
        );
      })(),
      true,
      ["edit", "copy"]
    ),
    buildRow(
      "postcode",
      <Hash className="w-4 h-4 text-gray-500" />,
      t("Postal Code"),
      <InputInline
        value={postcode}
        onFinish={(val: string) => handleUpdateField("postcode", val)}
        placeholder={t("Postal code")}
        variant="inline"
      />,
      postcode ? postcode : <span className="text-gray-400">{t("Set a value...")}</span>
    ),
    buildRowBucket("bucketId", <Folder className="w-4 h-4 text-gray-500" />, t("Group")),
    buildRow(
      "industry",
      <Factory className="w-4 h-4 text-gray-500" />,
      t("Industry"),
      <SelectInline
        autoOpen
        customTrigger={industryLabel}
        options={industryOptions}
        value={toStr(industry)}
        onChange={(val: string) => handleUpdateField("industry", val)}
      />,
      industryLabel
    ),
    // Social media fields
    buildRow(
      "linkedIn",
      <Linkedin className="w-4 h-4 text-gray-500" />,
      t("LinkedIn"),
      <InputInline
        value={linkedIn}
        onFinish={(val: string) => handleUpdateField("linkedIn", val)}
        placeholder={t("Set a link (e.g., linkedin.com/in/username)")}
        variant="inline"
      />,
      linkedIn ? (
        <a
          href={linkedIn.startsWith("http") ? linkedIn : `https://${linkedIn}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(linkedIn)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "instagram",
      <Instagram className="w-4 h-4 text-gray-500" />,
      t("Instagram"),
      <InputInline
        value={instagram}
        onFinish={(val: string) => handleUpdateField("instagram", val)}
        placeholder={t("Set a link (e.g., instagram.com/username)")}
        variant="inline"
      />,
      instagram ? (
        <a
          href={instagram.startsWith("http") ? instagram : `https://${instagram}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(instagram)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "twitter",
      <Twitter className="w-4 h-4 text-gray-500" />,
      t("Twitter"),
      <InputInline
        value={twitter}
        onFinish={(val: string) => handleUpdateField("twitter", val)}
        placeholder={t("Set a link (e.g., x.com/username)")}
        variant="inline"
      />,
      twitter ? (
        <a
          href={twitter.startsWith("http") ? twitter : `https://${twitter}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(twitter)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "facebook",
      <Facebook className="w-4 h-4 text-gray-500" />,
      t("Facebook"),
      <InputInline
        value={facebook}
        onFinish={(val: string) => handleUpdateField("facebook", val)}
        placeholder={t("Set a link (e.g., facebook.com/username)")}
        variant="inline"
      />,
      facebook ? (
        <a
          href={facebook.startsWith("http") ? facebook : `https://${facebook}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(facebook)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    // Custom Fields
    ...renderCustomFields(),
  ];

  if (!task.get("id")) {
    return (
      <div className="px-3">
        <div className="text-xs text-gray-500 py-4 text-center">{t("No information")}</div>
      </div>
    );
  }

  const visibleRows = visibleRowNumber === -1 ? rows : rows.slice(0, visibleRowNumber);

  return (
    <div className="px-3">
      {isLoading ? (
        <DetailPanelSkeleton rows={visibleRows.length} />
      ) : (
        visibleRows.map((row, idx) => <React.Fragment key={idx}>{row}</React.Fragment>)
      )}
    </div>
  );
};

const AccountDetailPanel: React.FC<AccountDetailPanelProps> = ({ projectId, contentType }) => {
  const { t } = useTranslation();

  const isLoadingTask = useSelector((state: any) => state.taskReducer.get("pending"));
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["task", contentType])) || fromJS({});

  const [collapsed, setCollapsed] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  const LeftIcon = useMemo(() => {
    return (
      <ChevronDown
        className={clsx(
          "w-4 h-4 transition-transform shrink-0",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    );
  }, [collapsed]);

  if (contentType !== "COMPANY") return null;

  const recordId = String(task.get("id") || "");
  const displayName =
    (task.getIn(["data", "name", "value"]) as string | undefined) ||
    (t("Untitled") as string);
  const iconUrl = (task.get("iconUrl") as string | undefined) || undefined;

  return (
    <Card
      bgClass={"bg-white"}
      borderClass={"border border-gray-200"}
      roundedClass={"rounded-[12px]"}
      shadow={"custom"}
      shadowClass={"shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]"}
    >
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-end gap-1 text-sm leading-5 font-medium text-gray-800 hover:text-gray-700"
        >
          {LeftIcon}
          {<span className="truncate">{t("Account Detail")}</span>}
        </button>
        <AskAiButton
          workspaceId={projectId}
          recordId={recordId}
          recordType="COMPANY"
          displayName={displayName}
          subtitle={t("Company") as string}
          avatarUrl={iconUrl}
          ariaLabel={t("Ask AI about this company") as string}
        />
      </div>
      {!collapsed && (
        <>
          <AccountDetail
            projectId={projectId}
            contentType={contentType}
            isLoading={isLoadingTask}
            task={task}
            visibleRowNumber={internalCollapsed ? 6 : -1}
          />
          <div className="px-3 pb-3 mt-2">
            <button
              type="button"
              onClick={() => setInternalCollapsed((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] leading-4 text-gray-500 hover:text-gray-700"
            >
              <span>{internalCollapsed ? t("Expand") : t("Collapse")}</span>
              {internalCollapsed ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronUp className="w-3 h-3" />
              )}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default AccountDetailPanel;
